import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useTournament } from "@/lib/TournamentContext";
import { LogOut, Trophy, Shield, ChevronDown, Menu, X } from "lucide-react";
import FeedbackButton from "@/components/app/FeedbackButton";
import NotificationBell from "@/components/app/NotificationBell";
import ThemeToggle from "@/components/app/ThemeToggle";

const NAV = [
  { type: "link", to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
  {
    type: "menu", label: "Tournament", testid: "nav-tournament",
    matchPrefixes: ["/standings", "/fixtures"],
    items: [
      { to: "/standings", label: "Standings", testid: "nav-standings" },
      { to: "/fixtures", label: "Fixtures", testid: "nav-fixtures" },
    ],
  },
  { type: "link", to: "/predict", label: "Competitions", testid: "nav-competitions", matchPrefixes: ["/predict", "/survivor", "/bracket", "/bracket-ko", "/fantasy", "/bonus"] },
  { type: "link", to: "/chat", label: "Chat", testid: "nav-chat" },
  { type: "link", to: "/leaderboard", label: "Leaderboard", testid: "nav-leaderboard", matchPrefixes: ["/leaderboard", "/groups"] },
  { type: "link", to: "/rules", label: "Rules", testid: "nav-rules" },
];

const isPrefixActive = (pathname, prefixes) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));

export default function TopNav() {
  const { user, logout } = useAuth();
  const { tournaments, active, setActive } = useTournament();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  return (
    <header className="sticky top-0 z-40 nav-surface backdrop-blur-xl border-b border-white/10 safe-top">
      <div className="px-3 md:px-8 py-3 flex items-center gap-3 md:gap-6">
        <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
          <div className="w-8 h-8 bg-primary-accent flex items-center justify-center flex-shrink-0">
            <Trophy size={18} />
          </div>
          <span className="font-display text-lg md:text-xl tracking-wider hidden sm:inline">JINXING SPORTS</span>
        </Link>

        {tournaments.length > 0 && (
          <div className="relative group hidden lg:block">
            <button
              data-testid="tournament-switcher"
              className="surface-card px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2 hover:border-white/30"
            >
              <span className="text-zinc-400">Tournament:</span>
              <span className="font-display text-base">{active?.short_code || active?.name}</span>
              <ChevronDown size={14} />
            </button>
            <div className="absolute left-0 top-full mt-1 hidden group-hover:block min-w-[260px] surface-card z-50">
              {tournaments.map((t) => (
                <button
                  key={t.tournament_id}
                  onClick={() => setActive(t.tournament_id)}
                  data-testid={`tournament-option-${t.short_code || t.tournament_id}`}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${t.tournament_id === active?.tournament_id ? "text-primary-accent" : ""}`}
                >
                  <div className="font-display tracking-wider text-base">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.sport.toUpperCase()} · {t.status}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Desktop nav */}
        <nav className="flex-1 hidden lg:flex items-center gap-1">
          {NAV.map((n) => {
            if (n.type === "menu") {
              const active = isPrefixActive(location.pathname, n.matchPrefixes);
              return (
                <div key={n.label} className="relative group">
                  <button
                    data-testid={n.testid}
                    className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap flex items-center gap-1 ${
                      active ? "border-primary-accent text-white" : "border-transparent text-zinc-400 hover:text-white"
                    }`}
                  >
                    {n.label} <ChevronDown size={12} />
                  </button>
                  <div className="absolute left-0 top-full pt-2 hidden group-hover:block min-w-[210px] z-50">
                    <div className="surface-card py-1">
                      {n.items.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          data-testid={it.testid}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-white/5 transition-colors ${
                              isActive ? "text-primary-accent" : "text-zinc-300"
                            }`
                          }
                        >
                          {it.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            const isActiveLink = n.matchPrefixes
              ? isPrefixActive(location.pathname, n.matchPrefixes)
              : location.pathname === n.to;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap ${
                  isActiveLink ? "border-primary-accent text-white" : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 ml-auto">
          {active?.short_code === "WC2026" && (
            <img
              src="/wc26-logo.png"
              alt="FIFA World Cup 2026"
              data-testid="wc26-emblem"
              title="FIFA World Cup 2026"
              className="h-9 md:h-10 w-auto rounded-md bg-black object-contain px-1.5 py-1 flex-shrink-0"
            />
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className="surface-card px-2 md:px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1 md:gap-2 hover:border-primary-accent text-primary-accent"
            >
              <Shield size={14} /> <span className="hidden md:inline">Admin</span>
            </NavLink>
          )}
          {user && (
            <NavLink to="/profile" data-testid="nav-profile" className="hidden md:flex flex-col items-end hover:text-primary-accent">
              <div className="text-xs uppercase tracking-widest text-zinc-500">{user.role}</div>
              <div className="font-display text-base leading-none">{user.name}</div>
            </NavLink>
          )}
          {user && <ThemeToggle compact />}
          {user && <NotificationBell />}
          {user && <FeedbackButton />}
          {user && (
            <button
              data-testid="logout-btn"
              onClick={() => setConfirmLogout(true)}
              className="w-8 h-8 surface-card hover:border-primary-accent flex items-center justify-center"
              title="Logout"
            >
              <LogOut size={14} />
            </button>
          )}
          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden w-9 h-9 surface-card flex items-center justify-center"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={18}/> : <Menu size={18}/>}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/10 nav-surface-solid safe-bottom" data-testid="mobile-menu">
          {tournaments.length > 0 && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="label-tag mb-1">Tournament</div>
              <select
                data-testid="mobile-tournament-switcher"
                value={active?.tournament_id || ""}
                onChange={(e) => setActive(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm focus:border-primary-accent focus:outline-none"
              >
                {tournaments.map((t) => (
                  <option key={t.tournament_id} value={t.tournament_id}>
                    {t.name} ({t.short_code || t.sport})
                  </option>
                ))}
              </select>
            </div>
          )}
          <nav className="flex flex-col py-2" onClick={() => setOpen(false)}>
            {NAV.map((n) => {
              if (n.type === "menu") {
                return (
                  <div key={n.label} className="py-1">
                    <div className="px-5 pt-2 pb-1 label-tag text-zinc-500">{n.label}</div>
                    {n.items.map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={`mobile-${it.testid}`}
                        className={({ isActive }) =>
                          `block pl-8 pr-5 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                            isActive ? "border-primary-accent bg-white/5 text-white" : "border-transparent text-zinc-400"
                          }`
                        }
                      >
                        {it.label}
                      </NavLink>
                    ))}
                  </div>
                );
              }
              const isActiveLink = n.matchPrefixes
                ? isPrefixActive(location.pathname, n.matchPrefixes)
                : location.pathname === n.to;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  className={`px-5 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                    isActiveLink ? "border-primary-accent bg-white/5 text-white" : "border-transparent text-zinc-400"
                  }`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      )}

      {confirmLogout && createPortal((
        <div
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="logout-confirm-modal"
          onClick={(e) => e.target === e.currentTarget && setConfirmLogout(false)}
        >
          <div className="surface-card max-w-sm w-full p-6">
            <div className="flex items-center gap-2 mb-2">
              <LogOut size={18} className="text-primary-accent" />
              <h3 className="font-display text-xl uppercase">Log out?</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-5">You&apos;ll need to sign in again to access your predictions and groups.</p>
            <div className="flex gap-3 justify-end">
              <button
                data-testid="logout-cancel-btn"
                onClick={() => setConfirmLogout(false)}
                className="px-4 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
              >Cancel</button>
              <button
                data-testid="logout-confirm-btn"
                onClick={() => { logout().then(() => navigate("/login")); }}
                className="bg-primary-accent text-white px-5 py-2 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)]"
              >Log Out</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </header>
  );
}
